import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { VertebraEnumAnatomicalOrder } from "../../../../classes/enums/AnnotationsEnum";
import { ButtonSize } from "../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import {
  DX_PATIENT_ORIENTATION_TO_PLANE,
  FileCategories,
  getOppositeDirection,
  MAP_BOTTOM_LABELS_TO_PATIENT_POSITION_LETTER,
  MODALITY,
  MODALITY_LABELS,
  morphologyDirections,
  standardizeModality,
} from "../../../../classes/enums/MedicalImagesEnum";
import { vertebraUtils } from "../../../../classes/utils/AnnotationsUtils";
import Button from "../../../../components/button/Button";
import DatePickerComponent from "../../../../components/datepicker/Datepicker";
import IconButton from "../../../../components/iconButton/IconButton";
import RadioButtonFlat from "../../../../components/radioButtons/RadioButtonFlat";
import RangeSlider from "../../../../components/rangeSlider/RangeSlider";
import Select from "../../../../components/select/Select";
import { TooltipOrientation } from "../../../../components/tooltip/Tooltip";
import ViewLoader from "../../../../components/viewLoader/ViewLoader";
import "../../../../errorAnimations.css";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import CornerstoneDicomViewer from "../../viewers/CornerstoneDicomViewer";
import NiftiViewer from "../../viewers/NiftiViewer";
import "./ImageValidation.css";

const getFileKey = file => {
  if (!file) return "file-null";

  const blob =
    typeof Blob !== "undefined" && file.file instanceof Blob ? file.file : null;
  const name = blob?.name || file.name || "";
  const size = blob?.size ?? file.file?.size ?? file.size ?? 0;
  const lastModified =
    blob?.lastModified ?? file.file?.lastModified ?? file.lastModified ?? 0;
  const instanceId = file.instanceId || "";
  const seriesId = file.seriesId || "";
  const studyId = file.studyId || "";
  const sopInstanceUid = file.sopInstanceUid || "";

  return [
    instanceId,
    seriesId,
    studyId,
    sopInstanceUid,
    name,
    size,
    lastModified,
  ]
    .map(value => value ?? "")
    .join("|");
};

const getGroupKey = group => {
  if (!group) return "group-null";

  const type = group.detectedType || group.modality || "";
  const fileKeys = (group.files || []).map(getFileKey).sort().join("||");
  const patientKey = group.patientInfo
    ? [
        group.patientInfo.firstName,
        group.patientInfo.lastName,
        group.patientInfo.birthDate,
        group.patientInfo.gender,
      ]
        .map(value => value ?? "")
        .join("|")
    : "";

  return [type, fileKeys, patientKey].join("::");
};

const EMPTY_VALUE = "-";
const ALL_DIRECTIONS = [EMPTY_VALUE, "L", "R", "A", "P", "I", "S"];

const getOrientationGroup = direction => {
  if (["A", "P"].includes(direction)) return "AP";
  if (["L", "R"].includes(direction)) return "LR";
  if (["I", "S"].includes(direction)) return "IS";
  return null;
};

const ImageValidation = ({ groups, onImport, onBack, onRemoveFile }) => {
  const { t } = useTranslation();
  const culture = useSelector(state => state.i18n.culture);

  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [currentSliceIndex, setCurrentSliceIndex] = useState(0);
  const [groupsInfo, setGroupsInfo] = useState({});
  const [numSlices, setNumSlices] = useState(0);
  const [viewerLoading, setViewerLoading] = useState(false);

  const [l5S1Variation, setL5S1Variation] = useState("None");
  const [vertebraError, setVertebraError] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const currentGroup = groups?.[currentGroupIndex] || null;
  const currentGroupKey = currentGroup ? getGroupKey(currentGroup) : null;
  const groupsCount = groups?.length ?? 0;
  const currentGroupInfo = currentGroupKey ? groupsInfo[currentGroupKey] : null;

  const isNiftiStructural3D = useMemo(() => {
    if (!currentGroup) return true; // assume 3D when no group
    const detectedType =
      currentGroup.detectedType || currentGroup.files?.[0]?.detectedType;
    if (detectedType !== FileCategories.NIFTI) return null; // not applicable for non-NIfTI
    const z = currentGroup.files?.[0]?.additionalMetadata?.dimensions?.z;
    return z == null || z > 1; // null z → unknown → assume 3D
  }, [currentGroup]);

  const buildInitialGroupInfo = useCallback(group => {
    const modality = standardizeModality(group.modality) || "";
    const isC1DefaultModality =
      modality === MODALITY.DX ||
      modality === MODALITY.CR;
    // Default to C1 for DX and CR — the most common first visible vertebra
    const defaultVertebra = isC1DefaultModality
      ? String(vertebraUtils.getCentroidEntity("C1"))
      : "";


    return {
      modality,
      acquisitionDate: group.creationDate || null,
      orientation: group.orientation || "",
      dxPlane: group.dxPlane || "",
      vertebra: defaultVertebra,
      hasTransitionalVertebra: false,
      hasT13: false,
      hasS1Lumbarized: false,
      hasL5Sacralized: false,
      patientInfo: group.patientInfo || {
        firstName: null,
        lastName: null,
        birthDate: null,
        gender: null,
      },
    };
  }, []);

  const [selectState, setSelectState] = useState({
    left: EMPTY_VALUE,
    bottom: EMPTY_VALUE,
    slide: EMPTY_VALUE,
  });

  // Consolidated orientation state per group: { [groupKey]: { original: {...}, current: {...} } }
  const [groupOrientationStates, setGroupOrientationStates] = useState({});
  const prevGroupKeyRef = useRef(null);

  const [, setImageOrientationState] = useState({
    left: 1,
    bottom: 1,
  });

  const [availableSelectOptions] = useState({
    left: ALL_DIRECTIONS,
    bottom: ALL_DIRECTIONS,
    slide: ALL_DIRECTIONS,
  });

  const modalityOptions = Object.keys(MODALITY_LABELS).map(code => ({
    label: MODALITY_LABELS[code],
    value: code,
  }));

  const vertebraOptions = useMemo(() => {
    return Object.keys(VertebraEnumAnatomicalOrder)
      .map(key => {
        const vertebraName = VertebraEnumAnatomicalOrder[key];
        const centroidId = vertebraUtils.getCentroidEntity(vertebraName);

        return {
          label: vertebraName,
          value: centroidId,
        };
      })
      .filter(option => option.value !== null);
  }, []);

  const transitionalVertebraOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const t13Options = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const l6Options = [
    { label: "S1 Lumbarized", value: "S1L" },
    { label: "L5 Sacralized", value: "L5S" },
    { label: "None", value: "None" },
  ];

  const renderInfoLabel = (label, tooltipText) => (
    <span className="image-info-label-with-icon">
      <span>{label}</span>
      <IconButton
        icon={IconsEnum.INFO}
        title={tooltipText}
        className="image-info-info-button"
        onClick={e => e.stopPropagation()}
        isCircle
        size={ButtonSize.MICRO}
        isLightBlue={false}
        tooltipOrientation={TooltipOrientation.Right}
      />
    </span>
  );

  const setLoading = useCallback(loading => {
    setViewerLoading(loading);
  }, []);

  const setOrientationsWithCapture = useCallback(
    newState => {
      if (!currentGroupKey) return;

      const groupState = groupOrientationStates[currentGroupKey];
      const hasOriginal =
        groupState?.original &&
        (groupState.original.left !== EMPTY_VALUE ||
          groupState.original.bottom !== EMPTY_VALUE ||
          groupState.original.slide !== EMPTY_VALUE);

      const hasValidNewState =
        newState.left !== EMPTY_VALUE ||
        newState.bottom !== EMPTY_VALUE ||
        newState.slide !== EMPTY_VALUE;

      if (!hasOriginal && hasValidNewState) {
        // First detection: set both original and current
        setGroupOrientationStates(prev => ({
          ...prev,
          [currentGroupKey]: {
            original: { ...newState },
            current: { ...newState },
          },
        }));
        setSelectState(newState);
      } else if (hasOriginal && !groupState?.current) {
        // Has original but no current yet (edge case)
        setGroupOrientationStates(prev => ({
          ...prev,
          [currentGroupKey]: {
            ...prev[currentGroupKey],
            current: { ...newState },
          },
        }));
        setSelectState(newState);
      }
      // If both original and current exist, don't overwrite - user may have modified
    },
    [currentGroupKey, groupOrientationStates]
  );

  const isStateEmptyVolume = useCallback(() => {
    return (
      selectState.left === EMPTY_VALUE ||
      selectState.bottom === EMPTY_VALUE ||
      selectState.slide === EMPTY_VALUE
    );
  }, [selectState]);

  const isStateEmptyDx = useCallback(() => {
    return (
      selectState.left === EMPTY_VALUE || selectState.bottom === EMPTY_VALUE
    );
  }, [selectState]);

  const validateOrientationChange = useCallback(
    (position, newValue) => {
      if (newValue === EMPTY_VALUE) {
        return {
          valid: true,
          newState: { ...selectState, [position]: newValue },
        };
      }

      const newOrientationGroup = getOrientationGroup(newValue);
      const opposite = getOppositeDirection(newValue);

      let newState = { ...selectState, [position]: newValue };

      Object.keys(selectState).forEach(otherPosition => {
        if (otherPosition === position) return;

        const otherValue = selectState[otherPosition];
        if (otherValue === EMPTY_VALUE) return;

        const otherGroup = getOrientationGroup(otherValue);

        if (
          otherGroup === newOrientationGroup ||
          otherValue === newValue ||
          otherValue === opposite
        ) {
          newState[otherPosition] = EMPTY_VALUE;
        }
      });

      return { valid: true, newState };
    },
    [selectState]
  );

  const handleOrientationChange = useCallback(
    position => e => {
      const newValue = e.target.value;
      const validation = validateOrientationChange(position, newValue);

      if (validation.valid) {
        setSelectState(validation.newState);
        // Persist user change to groupOrientationStates
        if (currentGroupKey) {
          setGroupOrientationStates(prev => ({
            ...prev,
            [currentGroupKey]: {
              ...prev[currentGroupKey],
              current: validation.newState,
            },
          }));
        }
      }
    },
    [validateOrientationChange, currentGroupKey]
  );

  const handleMorphologyOrientationChange = e => {
    updateGroupInfo("orientation", e.target.value);
  };

  const getVolumeOrientation = useCallback(() => {
    const { left, bottom, slide } = selectState;

    const orientationObj = {
      row_left: left,
      column_bottom: bottom,
      first_slice: slide !== EMPTY_VALUE ? slide : undefined,
    };
    return JSON.stringify(orientationObj);
  }, [selectState]);

  const getDxOrientation = useCallback(() => {
    const { left, bottom } = selectState;

    const orientationObj = {
      row_left: left,
      column_bottom: bottom,
    };
    return JSON.stringify(orientationObj);
  }, [selectState]);

  const getDxPlane = useCallback(() => {
    const { left, bottom } = selectState;
    let dxPlane = "";
    dxPlane = `${
      DX_PATIENT_ORIENTATION_TO_PLANE[
        `${MAP_BOTTOM_LABELS_TO_PATIENT_POSITION_LETTER[left]},${MAP_BOTTOM_LABELS_TO_PATIENT_POSITION_LETTER[bottom]}`
      ] || ""
    }`;
    if (dxPlane) {
      return dxPlane;
    } else {
      return "";
    }
  }, [selectState]);

  const getOriginalOrientation = useCallback(
    (group = currentGroup) => {
      if (!group) return null;

      const key = getGroupKey(group);
      const modality = groupsInfo[key]?.modality;

      // Determine effective structural type for this group
      const groupDetectedType =
        group.detectedType || group.files?.[0]?.detectedType;
      const groupZ = group.files?.[0]?.additionalMetadata?.dimensions?.z;
      const groupIs3DNifti =
        groupDetectedType === FileCategories.NIFTI &&
        (groupZ == null || groupZ > 1);

      const is2D =
        modality === MODALITY.DX ||
        modality === MODALITY.CR;

      const is3D =
        modality === MODALITY.CT ||
        modality === MODALITY.MR ||
        (!modality && groupIs3DNifti); // unknown modality + 3D NIfTI → treat as volume

      if (!is2D && !is3D) return null;

      const originalState = groupOrientationStates[key]?.original;
      if (!originalState) return null;

      if (is2D) {
        const { left, bottom } = originalState;
        if (left === EMPTY_VALUE || bottom === EMPTY_VALUE) return null;
        return JSON.stringify({ row_left: left, column_bottom: bottom });
      } else {
        const { left, bottom, slide } = originalState;
        if (left === EMPTY_VALUE || bottom === EMPTY_VALUE) return null;
        return JSON.stringify({
          row_left: left,
          column_bottom: bottom,
          first_slice: slide !== EMPTY_VALUE ? slide : undefined,
        });
      }
    },
    [currentGroup, groupsInfo, groupOrientationStates]
  );

  const updateGroupInfo = useCallback(
    (field, value) => {
      if (!currentGroupKey) return;

      setGroupsInfo(prevInfo => {
        const currentInfo = prevInfo[currentGroupKey] || {};
        return {
          ...prevInfo,
          [currentGroupKey]: {
            ...currentInfo,
            [field]: value,
          },
        };
      });
    },
    [currentGroupKey]
  );

  useEffect(() => {
    if (!currentGroup || !currentGroupKey) return;

    const currentGroupDetectedType =
      currentGroup?.files[0]?.detectedType || currentGroup?.detectedType;

    const currentModality = currentGroupInfo?.modality;
    if (!currentModality) return;

    const is2dDicom =
      (currentModality === MODALITY.DX ||
        currentModality === MODALITY.CR) &&
      currentGroupDetectedType === FileCategories.DICOM;

    const is2dNifti =
      isNiftiStructural3D === false &&
      currentGroupDetectedType === FileCategories.NIFTI;

    const isVolumeDicom =
      currentModality === MODALITY.CT ||
      currentModality === MODALITY.MR ||
      currentGroupDetectedType === FileCategories.DICOM;

    const isVolumeNifti =
      isNiftiStructural3D === true &&
      currentGroupDetectedType === FileCategories.NIFTI;

    let isOrientationComplete = false;
    let orientationString = "";
    let dxPlane = "";

    if (is2dDicom) {
      if (!isStateEmptyDx()) {
        isOrientationComplete = true;
        orientationString = getDxOrientation();
        dxPlane = getDxPlane();
      }
    } else if (isVolumeDicom) {
      if (!isStateEmptyVolume()) {
        isOrientationComplete = true;
        orientationString = getVolumeOrientation();
      }
    } else if (is2dNifti) {
      if (!isStateEmptyDx()) {
        isOrientationComplete = true;
        orientationString = getDxOrientation();
        dxPlane = getDxPlane();
      }
    } else if (isVolumeNifti) {
      if (!isStateEmptyVolume()) {
        isOrientationComplete = true;
        orientationString = getVolumeOrientation();
      }
    }

    const currentOrientation = currentGroupInfo?.orientation || "";
    if (
      isOrientationComplete &&
      orientationString &&
      orientationString !== currentOrientation
    ) {
      if (is2dDicom || is2dNifti) {
        updateGroupInfo("dxPlane", dxPlane);
      }

      updateGroupInfo("modality", currentModality);
      updateGroupInfo("orientation", orientationString);
    }
  }, [
    selectState,
    currentGroupIndex,
    isStateEmptyDx,
    getDxOrientation,
    isStateEmptyVolume,
    getVolumeOrientation,
    updateGroupInfo,
    getDxPlane,
    currentGroup,
    currentGroupKey,
    currentGroupInfo,
    isNiftiStructural3D,
  ]);

  const setInitialSliceIndex = useCallback(() => {
    if (!groups || groups.length === 0) return;

    const targetGroup = groups[currentGroupIndex];
    if (!targetGroup) return;

    if (
      targetGroup.files[0]?.detectedType === FileCategories.DICOM &&
      targetGroup.files.length > 0
    ) {
      const middleIndex = Math.floor(targetGroup.files.length / 2);
      setCurrentSliceIndex(middleIndex);
    } else if (targetGroup.detectedType === FileCategories.NIFTI) {
      setCurrentSliceIndex(0);
    } else {
      setCurrentSliceIndex(0);
    }
  }, [currentGroupIndex, groups]);

  useEffect(() => {
    if (!groups || groups.length === 0) {
      setGroupsInfo({});
      setGroupOrientationStates({});
      setCurrentGroupIndex(0);
      return;
    }

    setGroupsInfo(prevInfo => {
      const nextInfo = {};

      groups.forEach(group => {
        const key = getGroupKey(group);
        const existing = prevInfo[key];
        nextInfo[key] = existing || buildInitialGroupInfo(group);
      });

      return nextInfo;
    });

    setGroupOrientationStates(prevStates => {
      const nextStates = {};
      const defaultOrientation = {
        left: EMPTY_VALUE,
        bottom: EMPTY_VALUE,
        slide: EMPTY_VALUE,
      };

      groups.forEach(group => {
        const key = getGroupKey(group);
        nextStates[key] = prevStates[key] || {
          original: { ...defaultOrientation },
          current: { ...defaultOrientation },
        };
      });

      return nextStates;
    });

    setCurrentGroupIndex(prevIndex => {
      if (groups.length === 0) {
        return 0;
      }
      if (prevIndex >= groups.length) {
        return Math.max(groups.length - 1, 0);
      }
      return prevIndex;
    });
  }, [groups, buildInitialGroupInfo]);

  useEffect(() => {
    setInitialSliceIndex();

    if (!currentGroupKey) return;

    const defaultOrientation = {
      left: EMPTY_VALUE,
      bottom: EMPTY_VALUE,
      slide: EMPTY_VALUE,
    };

    setGroupOrientationStates(prev => ({
      ...prev,
      [currentGroupKey]: prev[currentGroupKey] || {
        original: { ...defaultOrientation },
        current: { ...defaultOrientation },
      },
    }));
  }, [currentGroupKey, setInitialSliceIndex]);

  useEffect(() => {
    if (numSlices > 0) {
      const middleIndex = Math.floor(numSlices / 2);
      setCurrentSliceIndex(middleIndex);
    }
  }, [numSlices]);

  // Sync selectState when switching groups (not when selectState changes within same group)
  useEffect(() => {
    const defaultState = {
      left: EMPTY_VALUE,
      bottom: EMPTY_VALUE,
      slide: EMPTY_VALUE,
    };

    if (!currentGroupKey) {
      prevGroupKeyRef.current = null;
      setSelectState(defaultState);
      return;
    }

    // Only sync when actually switching groups
    if (prevGroupKeyRef.current === currentGroupKey) {
      return;
    }
    prevGroupKeyRef.current = currentGroupKey;

    // Restore from current (user-modified) state for this group
    const groupState = groupOrientationStates[currentGroupKey];
    const storedState = groupState?.current || defaultState;
    setSelectState(storedState);
  }, [currentGroupKey, groupOrientationStates]);

  useEffect(() => {
    let desiredVariation = "None";

    if (currentGroupInfo?.hasS1Lumbarized) {
      desiredVariation = "S1L";
    } else if (currentGroupInfo?.hasL5Sacralized) {
      desiredVariation = "L5S";
    }

    if (l5S1Variation !== desiredVariation) {
      setL5S1Variation(desiredVariation);
    }
  }, [currentGroupInfo, l5S1Variation]);

  // Handle window resize to trigger viewer re-render
  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      // Debounce resize events to avoid excessive re-renders
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setWindowDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 150); // 150ms debounce
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleDateChange = timestamp => {
    updateGroupInfo("acquisitionDate", timestamp);
  };

  const handleModalityChange = e => {
    const newModality = e.target.value;
    updateGroupInfo("modality", newModality);

    updateGroupInfo("orientation", "");
    updateGroupInfo("dxPlane", "");
    const isC1DefaultModality =
      newModality === MODALITY.DX ||
      newModality === MODALITY.CR;
    const defaultVertebra = isC1DefaultModality
      ? String(vertebraUtils.getCentroidEntity("C1"))
      : "";
    updateGroupInfo("vertebra", defaultVertebra);

    const defaultOrientation = {
      left: EMPTY_VALUE,
      bottom: EMPTY_VALUE,
      slide: EMPTY_VALUE,
    };

    setSelectState(defaultOrientation);

    if (currentGroupKey) {
      // Reset both original and current when modality changes
      setGroupOrientationStates(prev => ({
        ...prev,
        [currentGroupKey]: {
          original: { ...defaultOrientation },
          current: { ...defaultOrientation },
        },
      }));
      // Reset the ref so viewer can re-detect and set orientation
      prevGroupKeyRef.current = null;
    }
  };

  const handleVertebra = e => {
    updateGroupInfo("vertebra", e.target.value);
    setVertebraError(false);
  };

  const handleTransitionalVertebra = e => {
    const value = e.target.value === "yes";
    updateGroupInfo("hasTransitionalVertebra", value);

    if (!value) {
      setL5S1Variation("None");
      updateGroupInfo("hasT13", false);
      updateGroupInfo("hasS1Lumbarized", false);
      updateGroupInfo("hasL5Sacralized", false);
    }
  };

  const handleT13 = e => {
    const value = e.target.value === "yes";
    updateGroupInfo("hasT13", value);
  };

  const handleL5S1Variation = e => {
    const value = e.target.value;
    setL5S1Variation(value);
    updateGroupInfo("hasS1Lumbarized", value === "S1L");
    updateGroupInfo("hasL5Sacralized", value === "L5S");
  };

  const handleSliceChange = value => {
    setCurrentSliceIndex(Number(value));
  };

  const requiresVertebra = useCallback(() => {
    const modality = currentGroupInfo?.modality;
    return (
      modality === MODALITY.DX ||
      modality === MODALITY.CR
    );
  }, [currentGroupInfo]);

  const handleNextGroup = () => {
    if (!isCurrentGroupValid()) {
      if (requiresVertebra() && !currentGroupInfo?.vertebra) {
        setVertebraError(true);
        setTimeout(() => setVertebraError(false), 800);
      }
      return;
    }
    if (currentGroupIndex < groupsCount - 1) {
      setVertebraError(false);
      setCurrentGroupIndex(currentGroupIndex + 1);
    }
  };

  const handlePrevGroup = () => {
    if (currentGroupIndex > 0) {
      setVertebraError(false);
      setCurrentGroupIndex(currentGroupIndex - 1);
    }
  };

  const handleDeleteGroup = () => {
    if (!currentGroup || !currentGroupKey) return;

    if (typeof onRemoveFile === "function") {
      onRemoveFile(currentGroup);
    }

    if (groupsCount <= 1) {
      onBack();
      return;
    }

    setCurrentGroupIndex(prevIndex => {
      if (prevIndex >= groupsCount - 1) {
        return Math.max(groupsCount - 2, 0);
      }
      return prevIndex;
    });
  };

  const handleConfirmClick = () => {
    console.log("[ImageValidation] handleConfirmClick", {
      currentGroupIndex,
      groupsCount,
      isCurrentGroupValid: isCurrentGroupValid(),
      currentGroupInfo,
      currentGroup,
    });

    // Show vertebra error if the last group is missing a required vertebra
    if (!isCurrentGroupValid()) {
      console.log("[ImageValidation] blocked: current group is not valid", {
        requiresVertebra: requiresVertebra(),
        vertebra: currentGroupInfo?.vertebra,
      });
      if (requiresVertebra() && !currentGroupInfo?.vertebra) {
        setVertebraError(true);
        setTimeout(() => setVertebraError(false), 800);
      }
      return;
    }

    const processedGroups = groups
      .map(group => {
        const groupKey = getGroupKey(group);
        const groupInfo = groupsInfo[groupKey];
        if (!groupInfo) {
          console.log("[ImageValidation] missing groupInfo for group", {
            groupKey,
            group,
          });
          return null;
        }

        const originalOrientation = getOriginalOrientation(group);

        const updatedGroup = {
          ...group,
          modality: groupInfo.modality,
          orientation: groupInfo.orientation,
          originalOrientation: originalOrientation,
          vertebra: groupInfo.vertebra,
          hasT13: groupInfo.hasT13,
          hasS1Lumbarized: groupInfo.hasS1Lumbarized,
          hasL5Sacralized: groupInfo.hasL5Sacralized,
          dxPlane: groupInfo.dxPlane,
          creationDate: groupInfo.acquisitionDate,
          patientInfo: groupInfo.patientInfo,
        };

        const updatedFiles = group.files.map(file => ({
          ...file,
          modality: groupInfo.modality,
          orientation: groupInfo.orientation,
          originalOrientation: originalOrientation,
          vertebra: groupInfo.vertebra,
          hasT13: groupInfo.hasT13,
          hasS1Lumbarized: groupInfo.hasS1Lumbarized,
          hasL5Sacralized: groupInfo.hasL5Sacralized,
          dxPlane: groupInfo.dxPlane,
          creationDate: groupInfo.acquisitionDate,
          patientInfo: groupInfo.patientInfo,
        }));

        updatedGroup.files = updatedFiles;
        return updatedGroup;
      })
      .filter(group => group !== null);

    console.log("[ImageValidation] calling onImport", {
      processedGroupsCount: processedGroups.length,
      processedGroups: processedGroups.map(group => ({
        id: group.id,
        patientInfo: group.patientInfo,
        files: group.files?.map(file => ({
          name: file.name,
          patientInfo: file.patientInfo,
        })),
      })),
    });

    onImport(processedGroups);
  };

  const getActualFile = fileObj =>
    fileObj && fileObj.file instanceof Blob ? fileObj.file : fileObj;

  const handleDimRead = useCallback(dim => {
    setNumSlices(dim);
  }, []);

  const renderViewer = () => {
    try {
      if (!groups || groups.length === 0) {
        return null;
      }

      if (
        !currentGroup ||
        !currentGroup.files ||
        currentGroup.files.length === 0
      ) {
        return null;
      }

      const currentGroupDetectedType =
        currentGroup.detectedType || currentGroup.files[0].detectedType;

      const firstFile = getActualFile(currentGroup.files[0]);
      if (!firstFile || !firstFile.name) {
        return null;
      }
      const lowerName = firstFile.name.toLowerCase();

      if (currentGroupDetectedType === FileCategories.IMAGE) {
        const previewUrl = URL.createObjectURL(firstFile);
        return (
          <img
            key={`${currentGroupIndex}-${windowDimensions.width}-${windowDimensions.height}`}
            src={previewUrl}
            alt="Preview"
            className="image-preview"
            onLoad={() => URL.revokeObjectURL(previewUrl)}
          />
        );
      }

      if (currentGroupDetectedType === FileCategories.NIFTI) {
        const isVolumeModality = isNiftiStructural3D === true;

        return (
          <div className="dicom-viewer-container">
            <div className="dicom-viewer-area">
              {isVolumeModality ? (
                <div className="orientation-label-left">{selectState.left}</div>
              ) : (
                <Select
                  items={availableSelectOptions.left}
                  selectedItem={selectState.left}
                  onChange={handleOrientationChange("left")}
                  className="orientation-select-left"
                />
              )}
              <div className="dicom-viewer-column">
                <div className="top-orientation-label">
                  {/* opposite of bottom */}
                  {getOppositeDirection(selectState.bottom)}
                </div>
                <NiftiViewer
                  key={`${currentGroupIndex}-${windowDimensions.width}-${windowDimensions.height}`}
                  file={firstFile}
                  sliceIndex={currentSliceIndex}
                  setOrientations={setOrientationsWithCapture}
                  onDimRead={handleDimRead}
                  onLoadingChange={setLoading}
                />
                {isVolumeModality ? (
                  <div className="orientation-label-bottom">
                    {selectState.bottom}
                  </div>
                ) : (
                  <Select
                    items={availableSelectOptions.bottom}
                    selectedItem={selectState.bottom}
                    onChange={handleOrientationChange("bottom")}
                    className="orientation-select-bottom"
                  />
                )}
              </div>
              <div className="right-orientation-label">
                {/* opposite of left */}
                {getOppositeDirection(selectState.left)}{" "}
              </div>
            </div>
            {numSlices > 1 && (
              <div className="validation-slice-controls">
                {isVolumeModality ? (
                  <div className="orientation-label-slide">
                    {selectState.slide}
                  </div>
                ) : (
                  <Select
                    items={availableSelectOptions.slide}
                    selectedItem={selectState.slide}
                    onChange={handleOrientationChange("slide")}
                    className="orientation-select-slide"
                  />
                )}
                <RangeSlider
                  min={0}
                  max={numSlices - 1}
                  step={1}
                  rangeValue={currentSliceIndex}
                  onRangeValueChanged={handleSliceChange}
                  className="slice-slider"
                />{" "}
                <div className="slice-orientation-label">
                  {getOppositeDirection(selectState.slide)}
                </div>
              </div>
            )}
          </div>
        );
      }

      if (currentGroupDetectedType === FileCategories.DICOM) {
        const isVolumeModality =
          currentGroupInfo?.modality === MODALITY.CT ||
          currentGroupInfo?.modality === MODALITY.MR;

        return (
          <div className="dicom-viewer-container">
            <div className="dicom-viewer-area">
              {isVolumeModality ? (
                <div className="orientation-label-left">{selectState.left}</div>
              ) : (
                <Select
                  items={availableSelectOptions.left}
                  selectedItem={selectState.left}
                  onChange={handleOrientationChange("left")}
                  className="orientation-select-left"
                />
              )}
              <div className="dicom-viewer-column">
                <div className="top-orientation-label">
                  {/* opposite of bottom */}
                  {getOppositeDirection(selectState.bottom)}
                </div>

                <CornerstoneDicomViewer
                  key={`${currentGroupIndex}-${windowDimensions.width}-${windowDimensions.height}`}
                  files={currentGroup.files}
                  index={currentSliceIndex}
                  onLoadingChange={setLoading}
                  setOrientations={setOrientationsWithCapture}
                  setImageOrientationState={setImageOrientationState}
                  modality={currentGroup.modality}
                />
                {isVolumeModality ? (
                  <div className="orientation-label-bottom">
                    {selectState.bottom}
                  </div>
                ) : (
                  <Select
                    items={availableSelectOptions.bottom}
                    selectedItem={selectState.bottom}
                    onChange={handleOrientationChange("bottom")}
                    className="orientation-select-bottom"
                  />
                )}
              </div>
              <div className="right-orientation-label">
                {/* opposite of left */}
                {getOppositeDirection(selectState.left)}{" "}
              </div>
            </div>
            <div className="dicom-bottom-controls">
              {currentGroup.files.length > 1 && (
                <div className="validation-slice-controls">
                  {isVolumeModality ? (
                    <div className="orientation-label-slide">
                      {selectState.slide}
                    </div>
                  ) : (
                    <Select
                      items={availableSelectOptions.slide}
                      selectedItem={selectState.slide}
                      onChange={handleOrientationChange("slide")}
                      className="orientation-select-slide"
                    />
                  )}
                  <RangeSlider
                    min={0}
                    max={currentGroup.files.length - 1}
                    step={1}
                    rangeValue={currentSliceIndex}
                    onRangeValueChanged={handleSliceChange}
                    className="slice-slider"
                    disabled={viewerLoading}
                  />
                  <div className="slice-orientation-label">
                    {getOppositeDirection(selectState.slide)}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {}, [currentSliceIndex]);

  const getDisplayedFile = () => {
    if (
      !currentGroup ||
      !currentGroup.files ||
      currentGroup.files.length === 0
    ) {
      return null;
    }

    const firstFile = getActualFile(currentGroup.files[0]);
    let file = null;
    if (firstFile && firstFile.name) {
      const lowerName = firstFile.name.toLowerCase();

      if (lowerName.endsWith(".dcm") || lowerName.endsWith(".dicom")) {
        file = getActualFile(
          currentGroup.files[
            currentSliceIndex >= currentGroup.files.length
              ? 0
              : currentSliceIndex
          ]
        );
      } else {
        file = firstFile;
      }
    }
    return file;
  };

  const isCurrentGroupValid = useCallback(() => {
    if (!currentGroupKey) return false;
    const currentInfo = groupsInfo[currentGroupKey];

    switch (currentInfo?.modality) {
      case MODALITY.DX:
      case MODALITY.CR:
        return !!(
          currentInfo &&
          currentInfo.modality &&
          currentInfo.acquisitionDate &&
          currentInfo.orientation &&
          currentInfo.vertebra &&
          currentInfo.hasTransitionalVertebra !== undefined
        );
      case MODALITY.MR:
      case MODALITY.CT:
        return !!(
          currentInfo &&
          currentInfo.modality &&
          currentInfo.acquisitionDate &&
          currentInfo.orientation
        );
      case MODALITY.MO:
        return !!(
          currentInfo &&
          currentInfo.modality &&
          currentInfo.acquisitionDate &&
          currentInfo.orientation
        );
      default:
        return !!(
          currentInfo &&
          currentInfo.modality &&
          currentInfo.acquisitionDate &&
          currentInfo.orientation &&
          currentInfo.vertebra &&
          currentInfo.hasTransitionalVertebra !== undefined
        );
    }
  }, [groupsInfo, currentGroupKey]);

  useEffect(() => {
    if (currentGroupKey && groupsInfo[currentGroupKey]) {
    }
  }, [groupsInfo, currentGroupIndex, currentGroupKey]);

  return (
    <div className="image-validation">
      <div className="image-validation-title">
        Verify and Upload Image ({groupsCount > 0 ? currentGroupIndex + 1 : 0}{" "}
        of {groupsCount})
      </div>

      <div className="image-validation-content">
        <div className="image-validation-column info-area">
          <div className="image-info-wrapper">
            <div className="info-header">
              <div className="modal-subtitle">Verify The Information</div>
            </div>

            {/* Patient information section */}

            {/* Image metadata section */}
            <div className="image-info-container">
              <div className="image-info-row image-info-row-file">
                <label className="image-info-label">File:</label>
                <div className="image-info-file">
                  {getDisplayedFile()?.name || "No file selected"}
                </div>
                <IconButton
                  icon={IconsEnum.DELETE}
                  onClick={handleDeleteGroup}
                  title="Delete this file"
                  noBgColor={true}
                />
              </div>
              <div className="image-info-row">
                <label className="image-info-label">{t("379")}</label>
                <Select
                  items={modalityOptions}
                  selectedItem={currentGroupInfo?.modality}
                  onChange={handleModalityChange}
                  placeholder={t("380")}
                />
              </div>
              <div className="image-info-row">
                <label className="image-info-label">{t("381")}</label>
                <div className="datepicker-wrapper">
                  <DatePickerComponent
                    handleChange={handleDateChange}
                    timestamp={currentGroupInfo?.acquisitionDate}
                    placeholderText={t("382")}
                    locale={culture}
                    showPlaceholder={true}
                  />
                </div>
              </div>
              {currentGroupInfo?.modality === MODALITY.MO && (
                <div className="image-info-row">
                  <div className="image-info-label">{t("383")}</div>
                  <Select
                    items={morphologyDirections}
                    selectedItem={currentGroupInfo?.orientation || ""}
                    onChange={handleMorphologyOrientationChange}
                    placeholder={t("384")}
                  />
                </div>
              )}
              {currentGroupInfo?.modality !== MODALITY.MO &&
                currentGroupInfo?.modality !== MODALITY.CT &&
                currentGroupInfo?.modality !== MODALITY.MR && (
                  <>
                    <div className="image-info-row">
                      <div className="image-info-label image-info-label-with-icon">
                        <span>{t("385")}</span>
                        <IconButton
                          icon={IconsEnum.INFO}
                          title={t("387")}
                          className="image-info-info-button"
                          onClick={e => e.stopPropagation()}
                          isCircle
                          size={ButtonSize.MICRO}
                          isLightBlue={false}
                          tooltipOrientation={TooltipOrientation.Right}
                        />
                      </div>
                      <Select
                        items={vertebraOptions}
                        selectedItem={currentGroupInfo?.vertebra || ""}
                        onChange={handleVertebra}
                        placeholder={t("386")}
                        className={
                          vertebraError && !currentGroupInfo?.vertebra
                            ? "error-input error-input-animate"
                            : ""
                        }
                      />
                    </div>
                    <div className="Vertebra-select-info">
                      <RadioButtonFlat
                        options={transitionalVertebraOptions}
                        selectedOption={
                          currentGroupInfo?.hasTransitionalVertebra
                            ? "yes"
                            : "no"
                        }
                        onChange={handleTransitionalVertebra}
                        name="transitionalVertebra"
                        displayName={renderInfoLabel(
                          "Are there any transitional vertebrae?",
                          t("457")
                        )}
                      />

                      {currentGroupInfo?.hasTransitionalVertebra && (
                        <div className="Vertebra-options">
                          <div className="Vertebra-select-info">
                            <RadioButtonFlat
                              options={t13Options}
                              selectedOption={
                                currentGroupInfo?.hasT13 ? "yes" : "no"
                              }
                              onChange={handleT13}
                              name="t13"
                              displayName={renderInfoLabel(
                                "Is T13 present?",
                                t("458")
                              )}
                            />
                          </div>

                          <div className="Vertebra-select-info">
                            <RadioButtonFlat
                              options={l6Options}
                              selectedOption={l5S1Variation || "None"}
                              onChange={handleL5S1Variation}
                              name="l5S1Variation"
                              displayName={renderInfoLabel(
                                "Is a lumbosacral transitional vertebra present?",
                                t("459")
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
            </div>
          </div>

          <div className="navigation-buttons">
            {groupsCount > 1 && (
              <>
                {currentGroupIndex > 0 && (
                  <Button
                    value="Previous"
                    onClick={handlePrevGroup}
                    className="prev-button"
                  />
                )}
                {currentGroupIndex < groupsCount - 1 && (
                  <Button
                    value={t("151")}
                    onClick={handleNextGroup}
                    disabled={!isCurrentGroupValid()}
                    className="next-button"
                  />
                )}
              </>
            )}

            {groupsCount > 0 &&
              (currentGroupIndex === groupsCount - 1 || groupsCount === 1) && (
                <Button
                  value={t("151")}
                  onClick={handleConfirmClick}
                  disabled={!isCurrentGroupValid()}
                  className="confirm-button"
                />
              )}
          </div>
        </div>
        <div className="image-validation-column image-area">
          <div className="modal-subtitle"> Verify Orientation</div>
          <div className="image-preview-container">
            <ViewLoader isLoading={viewerLoading} message="Loading image...">
              {renderViewer()}
            </ViewLoader>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageValidation;
